import React from 'react';
import Popup from '../../general/Popup.tsx';
import { SecondaryButton } from '../../general/SecondaryButton.tsx';
import { MoneyLabel } from '../../general/MoneyLabel.tsx';
import type { BackendAPITypes } from '../../../generated/blueprint.ts';
import { toDecimalFromMoney } from '../../../lib/Money.ts';
import { formatDate } from '../../../lib/utils.ts';
import { DEFAULT_CURRENCY } from '../../../types/constants.ts';

export type FixedAssetDetails = BackendAPITypes.BACKENDPreHandleSyncApiV1PartnerGetFixedAssetDetailsGetResponseBody;

type LinkedBill = NonNullable<FixedAssetDetails['linked_bill']>;

type Props = {
    open: boolean;
    onClose: () => void;
    details: FixedAssetDetails | undefined;
    isLoading: boolean;
    isError: boolean;
    onOpenBill?: (documentId: string) => void;
};

type RowProps = {
    label: string;
    value: React.ReactNode;
};

function Row({ label, value }: RowProps) {
    return (
        <div className="flex justify-between gap-4 py-1.5 border-b border-gray-100 last:border-b-0">
            <dt className="text-sm text-gray-500">{label}</dt>
            <dd className="text-sm text-gray-900 text-right">{value}</dd>
        </div>
    );
}

type EuroCentsProps = {
    cents: number;
};

function EuroCents({ cents }: EuroCentsProps) {
    return <MoneyLabel value={cents / 100} currency={DEFAULT_CURRENCY} showCurrency />;
}

type LinkedBillSummaryProps = {
    linkedBill: LinkedBill;
    onOpenBill?: (documentId: string) => void;
};

function LinkedBillSummary({ linkedBill, onOpenBill }: LinkedBillSummaryProps) {
    const reference = [
        linkedBill.invoice_number,
        linkedBill.invoice_date ? formatDate(linkedBill.invoice_date) : null,
    ].filter(Boolean).join(' · ');
    const total = linkedBill.total_amount_payable;

    return (
        <div className="flex items-center justify-between gap-4">
            <div className="text-sm text-gray-900">
                <p>{linkedBill.relation_name || 'Onbekende relatie'}</p>
                <p className="text-gray-500">
                    {reference}
                    {reference && total ? ' · ' : null}
                    {total && <MoneyLabel value={toDecimalFromMoney(total)} currency={total.currency} showCurrency />}
                </p>
            </div>
            {onOpenBill && (
                <SecondaryButton title="Open factuur" onClick={() => onOpenBill(linkedBill.document_id)} />
            )}
        </div>
    );
}

export function FixedAssetPreviewPopup({ open, onClose, details, isLoading, isError, onOpenBill }: Props) {
    function renderBody() {
        if (isError) {
            return (
                <p className="py-4 text-sm text-red-600" data-testid="fixed-asset-preview-error">
                    Het vaste actief kon niet worden geladen. Probeer het later opnieuw.
                </p>
            );
        }
        if (isLoading || !details) {
            return <p className="py-4 text-sm text-gray-500" data-testid="fixed-asset-preview-loading">Laden…</p>;
        }
        const linkedBill = details.linked_bill;
        return (
            <div className="py-2 space-y-4" data-testid="fixed-asset-preview">
                <dl>
                    <Row label="Aanschafdatum" value={formatDate(details.purchase_date)} />
                    <Row label="Aanschafwaarde" value={<EuroCents cents={details.purchase_value_cents} />} />
                    <Row label="Restwaarde" value={<EuroCents cents={details.residual_value_cents} />} />
                    <Row label="Afschrijven in maanden" value={details.economic_lifetime_months} />
                    <Row label="Afschrijving per maand" value={<EuroCents cents={details.monthly_depreciation_cents} />} />
                    <Row
                        label={`Boekwaarde (afschrijvingsschema) per ${formatDate(details.book_value_date)}`}
                        value={<EuroCents cents={details.book_value_cents} />}
                    />
                    <Row label="Afgeschreven maanden" value={details.months_depreciated} />
                    <Row
                        label="Laatste afschrijving"
                        value={details.depreciation_end_date ? formatDate(details.depreciation_end_date) : '—'}
                    />
                    <Row label="Rekening activa" value={details.balance_sheet_gl_account_label} />
                    <Row label="Rekening afschrijvingen" value={details.profit_and_loss_gl_account_label} />
                    {details.subledger && <Row label="Subadministratie" value={details.subledger.name} />}
                </dl>

                <div className="rounded border border-gray-200 p-3">
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-2">Gekoppelde factuur</p>
                    {linkedBill ? (
                        <LinkedBillSummary linkedBill={linkedBill} onOpenBill={onOpenBill} />
                    ) : (
                        <p className="text-sm text-gray-500">Geen factuur gekoppeld.</p>
                    )}
                </div>
            </div>
        );
    }

    return (
        <Popup
            open={open}
            onClose={onClose}
            onConfirm={onClose}
            title={details?.name ?? 'Vast actief'}
            confirmButtonDisplayLabel="Sluiten"
            disableCancelButton
            Icon={null}
        >
            {renderBody()}
        </Popup>
    );
}
